import { useEffect, useMemo, useState } from 'react';

import { getWindowSafe } from '@change-corgi/core/window';

type FullWidthLayoutStyles = {
	width: string | string[];
	marginLeft: string | string[];
	marginRight: string | string[];
};

/**
 * Hook to calculate full-width layout styles that account for scrollbar width.
 *
 * Returns responsive styles that:
 * - On mobile: Use 100vw with standard breakout technique (no scrollbar issues)
 * - On desktop: Use calculated width that excludes actual scrollbar width
 *
 * This avoids horizontal scroll issues caused by 100vw including scrollbar width
 * while maintaining full viewport coverage.
 *
 * SSR-safe: Uses getWindowSafe() and fallback values during server-side rendering.
 * Uses ResizeObserver when available for efficient resize detection, with fallback
 * to window resize events for older browsers.
 */
export function useFullWidthLayout(): FullWidthLayoutStyles {
	const [scrollbarWidth, setScrollbarWidth] = useState(17); // Default fallback

	const windowObject = getWindowSafe();

	const isResizeObserverSupported = useMemo(
		() => typeof windowObject !== 'undefined' && 'ResizeObserver' in windowObject,
		[windowObject],
	);

	useEffect(() => {
		if (!windowObject) {
			return undefined;
		}

		const calculateScrollbarWidth = () => {
			// Calculate actual scrollbar width by comparing window width vs document width
			const windowWidth = windowObject.innerWidth;
			const documentWidth = windowObject.document.documentElement.clientWidth;
			const actualScrollbarWidth = windowWidth - documentWidth;

			setScrollbarWidth(actualScrollbarWidth);
		};

		// Calculate on mount
		calculateScrollbarWidth();

		// Use ResizeObserver on document element if supported, otherwise fall back to window resize
		if (isResizeObserverSupported) {
			const observer = new windowObject.ResizeObserver(() => {
				calculateScrollbarWidth();
			});

			observer.observe(windowObject.document.documentElement);

			return () => {
				observer.disconnect();
			};
		}

		// Fallback to window resize event if ResizeObserver not supported
		const handleResize = () => {
			calculateScrollbarWidth();
		};

		windowObject.addEventListener('resize', handleResize);

		return () => {
			windowObject.removeEventListener('resize', handleResize);
		};
	}, [windowObject, isResizeObserverSupported]);

	// Use fallback values during SSR, calculated values on client
	const desktopWidth = !windowObject ? 'calc(100vw - 17px)' : `calc(100vw - ${scrollbarWidth}px)`;
	const desktopMargin = !windowObject
		? 'calc((100vw - 17px) / -2 + 50%)'
		: `calc((100vw - ${scrollbarWidth}px) / -2 + 50%)`;

	return {
		// Mobile: 100vw, Desktop: calculated width excluding scrollbar
		width: ['100vw', desktopWidth],
		// Mobile: standard breakout, Desktop: adjusted for scrollbar-excluded width
		marginLeft: ['calc(-50vw + 50%)', desktopMargin],
		marginRight: ['calc(-50vw + 50%)', desktopMargin],
	};
}
